import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Shield, CheckCircle2, AlertCircle } from 'lucide-react';

interface VerificationGateModalProps {
  open: boolean;
  onClose: () => void;
  onGetVerified: () => void;
  isPending?: boolean;
  isRejected?: boolean;
}

export const VerificationGateModal: React.FC<VerificationGateModalProps> = ({
  open,
  onClose,
  onGetVerified,
  isPending = false,
  isRejected = false,
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            {isPending ? 'Verification Pending' : isRejected ? 'Verification Required' : 'Verify Your Humanity'}
          </DialogTitle>
          <DialogDescription>
            {isPending 
              ? "We're reviewing your verification request. You'll be notified once approved!"
              : isRejected
              ? "Your previous verification was not approved. Please submit a new request with updated proof."
              : "To maintain a community of real creators, we require verification before posting."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Status Indicator */}
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="flex justify-center"
          >
            <div className={`
              w-24 h-24 rounded-full flex items-center justify-center
              ${isPending ? 'bg-yellow-500/10' : isRejected ? 'bg-destructive/10' : 'bg-primary/10'}
            `}>
              {isPending ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <AlertCircle className="w-12 h-12 text-yellow-500" />
                </motion.div>
              ) : isRejected ? (
                <AlertCircle className="w-12 h-12 text-destructive" />
              ) : (
                <Shield className="w-12 h-12 text-primary" />
              )}
            </div>
          </motion.div>

          {/* Explanation */}
          <div className="space-y-3 text-sm text-muted-foreground">
            {!isPending && (
              <>
                <p className="font-medium text-foreground">What we need:</p>
                <ul className="space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                    <span>A link to artwork you've created (anywhere online)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                    <span>Proof you made it (layers, WIP shots, behind-the-scenes) — it doesn't need to be complex</span>
                  </li>
                </ul>
                <p className="pt-2 text-xs">
                  This helps us keep FOMO focused on real human creators and their authentic work. We're not judging your skill level—just verifying you're human.
                </p>
              </>
            )}

            {isPending && (
              <div className="text-center">
                <p className="font-medium text-foreground mb-2">Hang tight!</p>
                <p>
                  Your verification request is being reviewed by our team. 
                  This usually takes a few hours. You'll receive a notification once approved.
                </p>
              </div>
            )}

            {isRejected && (
              <div className="text-center">
                <p className="font-medium text-foreground mb-2">Let's try again</p>
                <p>
                  Submit a new verification request with clearer proof that you created the artwork.
                  Make sure your proof clearly shows your process.
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {!isPending && (
              <Button 
                onClick={onGetVerified} 
                className="flex-1"
                size="lg"
              >
                {isRejected ? 'Submit New Request' : 'Get Verified'}
              </Button>
            )}
            <Button 
              onClick={onClose} 
              variant={isPending ? "default" : "outline"}
              className={isPending ? "flex-1" : "flex-1"}
              size="lg"
            >
              {isPending ? 'Got It' : 'Maybe Later'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
